import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, MessageCircle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_TEMPLATES = [
    { title: 'Welcome!', message: 'Welcome to BETohalics! Thanks for joining — let us know if you need any help getting started.' },
    { title: 'Cashout ready', message: 'Your cashout has been approved! Check your transactions for details.' },
    { title: 'Quick check-in', message: 'Hey! Just checking in — let us know if you need anything or have any questions.' },
    { title: 'Bonus available', message: 'A bonus is waiting on your account. Log in to claim it!' },
];

const fmtTime = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        if (diff < 60_000) return 'just now';
        if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
        if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
        return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT';
    } catch { return ''; }
};

const SendMessageDialog = ({ open, onClose, user }) => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [thread, setThread] = useState([]);
    const [loadingThread, setLoadingThread] = useState(false);
    const scrollRef = useRef(null);

    const reset = () => { setTitle(''); setMessage(''); };
    const close = () => { reset(); onClose?.(); };
    const applyTemplate = (t) => { setTitle(t.title); setMessage(t.message); };

    // Load conversation history when the dialog opens
    useEffect(() => {
        if (!open || !user?.id) { setThread([]); return; }
        let cancelled = false;
        (async () => {
            setLoadingThread(true);
            try {
                const { data } = await axios.get(`${API}/admin/messages/thread/${user.id}?limit=50`, { withCredentials: true });
                if (!cancelled) setThread(data.messages || []);
            } catch {
                if (!cancelled) setThread([]);
            } finally {
                if (!cancelled) setLoadingThread(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, user?.id]);

    // Auto-scroll to newest message whenever thread updates
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [thread]);

    const send = async () => {
        if (!title.trim() || !message.trim()) {
            toast.error('Both title and message are required');
            return;
        }
        setSending(true);
        try {
            await axios.post(
                `${API}/admin/message`,
                { user_id: user.id, title: title.trim(), message: message.trim() },
                { withCredentials: true },
            );
            // Refetch thread so the new line shows immediately
            const { data: t } = await axios.get(`${API}/admin/messages/thread/${user.id}?limit=50`, { withCredentials: true });
            setThread(t.messages || []);
            reset();
            toast.success('Sent');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    if (!user) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
            <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg" data-testid="send-message-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-[#FFD700]" />
                        Conversation with {user.name}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2] text-xs">
                        In-app notification + browser push (if they've enabled it).
                    </DialogDescription>
                </DialogHeader>

                {/* Conversation history */}
                <div
                    ref={scrollRef}
                    className="bg-black/30 border border-white/5 rounded-lg p-2 h-48 overflow-y-auto space-y-2"
                    data-testid="message-thread"
                >
                    {loadingThread && thread.length === 0 ? (
                        <p className="text-[#A0AAB2] text-xs text-center py-6">Loading…</p>
                    ) : thread.length === 0 ? (
                        <p className="text-[#A0AAB2] text-xs text-center py-6">No messages yet — start the conversation below.</p>
                    ) : (
                        thread.map((m) => {
                            const isAdmin = m.direction === 'admin_to_user';
                            return (
                                <div key={m.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                                    <div
                                        className={`max-w-[78%] rounded-lg px-3 py-1.5 text-xs ${
                                            isAdmin
                                                ? 'bg-[#FFD700]/15 border border-[#FFD700]/30 text-white'
                                                : 'bg-white/5 border border-white/10 text-white'
                                        }`}
                                        data-testid={`thread-msg-${m.direction}`}
                                    >
                                        <p className="whitespace-pre-wrap break-words leading-snug">{m.message}</p>
                                        <p className={`text-[9px] mt-0.5 ${isAdmin ? 'text-[#FFD700]/70' : 'text-[#A0AAB2]'}`}>
                                            {isAdmin ? (m.sender_name || 'Admin') : (m.sender_name || user.name)} · {fmtTime(m.created_at)}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Composer */}
                <div className="space-y-3 mt-1">
                    <div className="flex flex-wrap gap-1.5">
                        {QUICK_TEMPLATES.map((t, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => applyTemplate(t)}
                                className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-[#A0AAB2] hover:bg-[#FFD700]/10 hover:text-[#FFD700] border border-white/10 transition-colors"
                                data-testid={`message-template-${i}`}
                            >
                                {t.title}
                            </button>
                        ))}
                    </div>

                    <div>
                        <Label className="text-white text-xs">Title</Label>
                        <Input
                            value={title}
                            maxLength={80}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Short subject (max 80 chars)"
                            className="mt-1 bg-[#121212] border-white/10 text-white text-sm h-9"
                            data-testid="send-message-title"
                        />
                    </div>

                    <div>
                        <Label className="text-white text-xs">Message</Label>
                        <textarea
                            value={message}
                            maxLength={500}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="What you want to tell them…"
                            rows={3}
                            className="mt-1 w-full px-3 py-2 rounded-md bg-[#121212] border border-white/10 text-white text-sm focus:border-[#FFD700] focus:outline-none focus:ring-1 focus:ring-[#FFD700]/30 resize-none"
                            data-testid="send-message-body"
                        />
                        <p className="text-[10px] text-[#A0AAB2] mt-0.5 text-right tabular-nums">{message.length} / 500</p>
                    </div>

                    <div className="flex gap-2">
                        <Button onClick={close} variant="outline" className="flex-1 bg-transparent border-white/10 text-[#A0AAB2] hover:bg-white/5"
                            data-testid="send-message-cancel">
                            Close
                        </Button>
                        <Button
                            onClick={send}
                            disabled={sending || !title.trim() || !message.trim()}
                            className="flex-1 bg-[#FFD700] text-black hover:bg-[#FFC700] font-bold"
                            data-testid="send-message-submit"
                        >
                            <Send className="w-4 h-4 mr-1.5" />
                            {sending ? 'Sending…' : 'Send'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SendMessageDialog;
